import { Page, View, Image, Text, Link } from '@react-pdf/renderer';
import { styles } from './styles';
import {
  Portfolio,
  PublicEventDetailsResponse,
  ResponsePaginationType,
} from '@hubs101/booking-api-skd-client/lib/types';
import { useTranslation } from 'react-i18next';
import { globalStyles } from '../../styles';
import { APP_HOST } from '@utils/apiHooks';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import weekday from 'dayjs/plugin/weekday';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { SafeImage } from '../../components/SafeImage';

dayjs.extend(utc);
dayjs.extend(weekday);
dayjs.extend(advancedFormat);

export interface IcebreakerDinnerPageProps {
  event: PublicEventDetailsResponse;
  coLocatedEvents: PublicEventDetailsResponse[] | undefined;
  portfolios: ResponsePaginationType<Portfolio> | undefined;
  header: JSX.Element;
  footer: JSX.Element;
}

const icons = ['\uf57b', '\uf2e7']; // drink, dinner

const formatDateTime = (dateStr: string, language: string, addDays = 0) => {
  const d = dayjs.utc(dateStr).locale(language).add(addDays, 'day');
  return {
    date: d.format('DD, MMMM YYYY'),
    time: d.format('HH:mm'),
  };
};

const EventBlock = ({
  icon,
  title,
  subtitle,
  paragraphs,
  listItems,
  buttonLabel,
  imageSrc,
  date,
  time,
  location,
  event,
}: {
  icon: string;
  title: string;
  subtitle: string;
  paragraphs?: string[];
  listItems?: string[];
  buttonLabel: string;
  imageSrc: string;
  date: string;
  time: string;
  location: { street?: string; postalCode?: string; city?: string };
  event: PublicEventDetailsResponse;
}) => {
  const { t } = useTranslation();

  return (
    <View style={styles.itemWrapper}>
      {/* Row 1 */}
      <View style={styles.topRow}>
        <View style={{ width: '28%', flexShrink: 1, flexWrap: 'wrap' }}>
          <View style={{ display: 'flex', flexDirection: 'row' }}>
            <Text style={styles.icon}>{icon}</Text>
            <Text style={styles.itemTitle}>{title}</Text>
          </View>

          <Text
            style={{
              ...styles.text,
              fontSize: 9,
              marginTop: 6,
              marginBottom: 4,
            }}
          >
            {subtitle}
          </Text>

          <View style={{ display: 'flex', flexDirection: 'row', gap: 5 }}>
            <Text style={styles.boldText}>{t('when')}:</Text>
            <View>
              <Text style={styles.text}>{date}</Text>
              <Text style={styles.text}>
                {time} {t('clock')}
              </Text>
            </View>
          </View>

          <View style={{ display: 'flex', flexDirection: 'row', gap: 5 }}>
            <Text style={styles.boldText}>{t('where')}:</Text>
            <View>
              <Text style={styles.text}>{location.street}</Text>
              <Text style={styles.text}>
                {(location.postalCode ?? '') + ' ' + (location.city ?? '')}
              </Text>
            </View>
          </View>
        </View>

        <View style={{ width: '65%', maxWidth: 350 }}>
          <SafeImage
            style={styles.itemImage}
            src={imageSrc}
            debugLabel="icebreaker-sub-image"
          />
        </View>
      </View>

      {/* Row 2 */}
      <View style={styles.fullRow}>
        {paragraphs?.map((p, i) => (
          <Text key={i} style={styles.itemDescription}>
            {p}
          </Text>
        ))}
        {listItems?.map((item, i) => (
          <View
            key={i}
            style={{
              ...styles.itemListItem,
              display: 'flex',
              flexDirection: 'row',
              gap: 4,
            }}
          >
            <Text>{'\u2022'}</Text>
            <Text>{item}</Text>
          </View>
        ))}
      </View>

      {/* Row 3 */}
      <View style={styles.buttonWrapper}>
        <Link
          src={
            buttonLabel === t('icebreaker-button')
              ? `${APP_HOST}/events/${event?.slug}/icebreaker`
              : `${APP_HOST}/events/${event?.slug}/evening-networking`
          }
          style={styles.buttonText}
        >
          {buttonLabel}
        </Link>
      </View>
    </View>
  );
};

const IcebreakerDinnerPage = ({
  event,
  coLocatedEvents,
  portfolios,
  header,
  footer,
}: IcebreakerDinnerPageProps) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const icebreakerDateTime = formatDateTime(event?.start_date, lang);
  const dinnerDateTime = formatDateTime(event?.start_date, lang, 1);

  const location = event?.location?.marker ?? {};

  return (
    <Page size="A4" orientation="portrait" style={styles.page}>
      <View style={styles.backgroundContainer}>
        <SafeImage
          src="https://media.we-conect.com/images/pdf/agenda/pdf_agenda_icebreaker_hero.jpg"
          style={styles.backgroundImage}
          debugLabel="agenda-icebreaker-background"
        />
        <View>{header}</View>
        <View style={styles.overlayContent}>
          <Text style={styles.title}>{'Icebreaker & Dinner'}</Text>
          <View
            style={{
              ...globalStyles.lineBar,
              borderColor: 'white',
              marginLeft: 0,
              marginTop: 10,
            }}
          />
        </View>
      </View>

      <View style={styles.contentWrapper}>
        {/* Icebreaker */}
        <EventBlock
          icon={icons[0]}
          title={t('icebreaker')}
          subtitle={t('what-is-the-icebreaker-session')}
          paragraphs={[
            t('icebreaker-session-pdf-paragraph-1'),
            t('icebreaker-session-pdf-paragraph-2'),
            t('icebreaker-session-pdf-paragraph-3'),
          ]}
          buttonLabel={t('icebreaker-button')}
          imageSrc="https://media.we-conect.com/images/pdf/agenda/pdf_agenda_icebreaker_2.jpg"
          date={icebreakerDateTime.date}
          time={icebreakerDateTime.time}
          location={location}
          event={event}
        />

        {/* Dinner */}
        <EventBlock
          icon={icons[1]}
          title={t('dinner')}
          subtitle={t('bits-bites-bubbles-is-the-place-for-you')}
          paragraphs={[t('dinner-session-pdf-paragraph')]}
          listItems={[
            t('dinner-session-pdf-list-item-1'),
            t('dinner-session-pdf-list-item-2'),
            t('dinner-session-pdf-list-item-3'),
          ]}
          buttonLabel={t('dinner-button')}
          imageSrc="https://media.we-conect.com/images/pdf/agenda/pdf_agenda_icebreaker_3.jpg"
          date={dinnerDateTime.date}
          time={dinnerDateTime.time}
          location={location}
          event={event}
        />
      </View>

      {footer}
    </Page>
  );
};

export default IcebreakerDinnerPage;
